import {
    catchError,
    concat,
    EMPTY,
    ignoreElements,
    map,
    Observable,
    of,
    race,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs';
import { ofType } from 'src/core/engine/rxjs/ofType';
import { AppendCartState, CartActions, cartVariantAndId, CartMsg } from 'src/core/state/cart';
import { DataStatus } from 'src/core/engine/types';
import { AppendUserState, UserSession } from 'src/core/state/user';
import { execute } from 'swagger/ts/WorkwearExpressCurrencyCurrencyManagementV1UpdatePut';
import { DEFAULT_CURRENCY } from 'src/components/AppShell/app-shell.types';
import { Deps } from 'src/types/global-types';

import { Actions, BasketMsg, TypeMap } from '../basket.actions';

export function loadStepBasket(
    action$: Observable<any>,
    state$: Observable<AppendCartState<AppendUserState>>,
    deps: Deps,
): Observable<any> {
    return action$.pipe(
        ofType<Actions, TypeMap['Basket.LoadStep']>('Basket.LoadStep'),
        withLatestFrom(
            state$.pipe(map((s) => s?.cart?.status)),
            state$.pipe(map((s) => s?.user?.session)),
            state$.pipe(map((s) => (s as any)?.currency?.code)),
            cartVariantAndId(state$),
        ),
        switchMap(([, cartStatus, userSession, currencyCode, [, cartId]]) => {
            const code = typeof currencyCode === 'string' && currencyCode ? currencyCode : DEFAULT_CURRENCY;
            const isSignedIn = userSession === UserSession.SignedIn;

            /**
             * Listen for a fetchSuccess and follow up with a basket refresh.
             * After sign-in the cart may already be pending (userSignedInReaction),
             * so this path must still emit Basket.Refresh — otherwise the basket
             * page stays on its loading placeholder until a full reload.
             */
            const good = action$.pipe(
                ofType<CartActions>('Cart.FetchSuccess'),
                switchMap(() => {
                    const refresh$ = of(BasketMsg('Basket.Refresh'));

                    if (!cartId) {
                        return refresh$;
                    }

                    /**
                     * Set the currency code.
                     * Do not set currency if customer is guest - the guest currency endpoint
                     * does not exist on the backend (consistent with checkout behavior).
                     */
                    if (isSignedIn) {
                        return concat(
                            refresh$,
                            execute({ currencyCode: code }, deps).pipe(
                                catchError(() => EMPTY),
                                ignoreElements(),
                            ),
                        );
                    }

                    return refresh$;
                }),
            );

            /**
             * Listen for when a refresh failed.
             */
            const bad = action$.pipe(
                ofType<CartActions>('Cart.FetchError'),
                map(() => BasketMsg('Basket.Noop', 'Not refreshing since the cart had issues')),
            );

            /**
             * Listen to either exclusively
             */
            const eitherOr = race(good, bad).pipe(take(1));

            /**
             * If we're already pending, just wait
             */
            if (cartStatus === DataStatus.Pending) {
                return eitherOr;
            }

            /**
             * Otherwise trigger a refresh and _then_ wait
             */
            return concat(of(CartMsg('Cart.Refresh', { force: true })), eitherOr);
        }),
    );
}
